/* Invoice History → PDF report (Duncan 2026-10-15: "the same reporting and exporting that job
   cost has"). Route /ar/invoice-report?<the page's period and filters> (lib/invoiceHistory
   viewToParams), opened from Invoice History's Export menu: what's showing, or one job (job=).

   Laid out like the Project Status Report (PsrReportPage.js): US Letter sheets at 816 × 1056 px
   in a portal on <body>, the app hidden when printing, and the same buttons: Print / Save as PDF,
   Download Excel (lib/invoiceXlsx) and Email report (E3 people only; the PDF is drawn from these
   sheets by lib/psrPdf, which finds them by their .psr-sheet class). Page 1 has the five figures
   and one line per project; every invoice follows, by project. Rows are one line of fixed
   height, so the pages are counted rather than measured; a table that breaks repeats its head. */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getInvoiceHistory, emailInvoiceReport } from '../api';
import { customerOfJob } from '../lib/statement';
import { loadPsrFonts } from '../lib/psrData';
import {
  dateOf, daysToPay, describeView, fmtDay, payStatsOf, periodKey, projectStatus, selectView, statusMeta, todayCentral, viewFromParams, SLOW_DAYS,
} from '../lib/invoiceHistory';
import EmailReportDialog from '../components/report/EmailReportDialog';

/* ── the report's design tokens (PsrReportPage.js) ── */
const INK = '#1F1D1A';
const INK2 = '#4A463F';
const GREY = '#6B665E';
const LINE = '#D6D2CA';
const FILL = '#F5F3EF';
const GREEN_HEAD = '#41593A';
const GREEN_SPENT = '#5A7A4D';
const GREEN_GOOD = '#3F7A57';
const BLUE = '#2F5D8A';
const AMBER = '#94601F';
const RED = '#B3402E';
const SANS = "'Geist', system-ui, sans-serif";
const MONO = "'Geist Mono', 'SF Mono', Menlo, Consolas, monospace";
const TONE = { success: GREEN_GOOD, blue: BLUE, danger: RED, warning: AMBER, neutral: GREY };

/* ── page geometry: fixed-height blocks, so the pages can be counted ── */
const PAGE_W = 816;
const PAGE_H = 1056;
const PAD_TOP = 54;
const PAD_X = 60;
const CONTENT_H = 916;   // content top (54) to 16px above the footer rule (y 986)
const P1_HEAD = 186;     // head 96 + 14 + figures 76
const RUN_HEAD = 42;
const H = { title: 38, thead: 26, project: 36, ptotal: 30, group: 28, inv: 24 };

/* The flow of blocks after page 1's head, then onto pages: a table that breaks carries its
   title ("continued") and head to the next page; a section title, a project's group line and
   the first row after them stay together. */
function buildFlow(visible, oneJob) {
  const flow = [];
  if (!oneJob) {
    flow.push({ kind: 'title', section: 'projects' }, { kind: 'thead', section: 'projects' });
    visible.forEach((j) => flow.push({ kind: 'project', section: 'projects', j }));
    flow.push({ kind: 'ptotal', section: 'projects' });
  }
  flow.push({ kind: 'title', section: 'invoices' }, { kind: 'thead', section: 'invoices' });
  visible.forEach((j) => {
    if (!oneJob) flow.push({ kind: 'group', section: 'invoices', j });
    [...j.invoices]
      .sort((a, b) => String(dateOf(a) || '').localeCompare(String(dateOf(b) || '')) || String(a.number).localeCompare(String(b.number)))
      .forEach((i) => flow.push({ kind: 'inv', section: 'invoices', i, j }));
  });
  return flow;
}
export function paginateFlow(flow) {
  const pages = [[]];
  let y = P1_HEAD;
  const newPage = (section) => {
    pages.push([]);
    y = RUN_HEAD;
    if (section) {
      pages[pages.length - 1].push({ kind: 'title', section, continued: true }, { kind: 'thead', section });
      y += H.title + H.thead;
    }
  };
  for (let k = 0; k < flow.length; k += 1) {
    const it = flow[k];
    let need = H[it.kind];
    if (it.kind === 'title') need += H.thead + H[(flow[k + 2] || { kind: 'inv' }).kind] + (flow[k + 2] && flow[k + 2].kind === 'group' ? H.inv : 0);
    if (it.kind === 'group') need += H.inv;
    if (it.kind !== 'thead' && y + need > CONTENT_H) newPage(it.kind === 'title' ? null : it.section);
    pages[pages.length - 1].push(it);
    y += H[it.kind];
  }
  return pages;
}

const MINUS = '−';
const money = (v) => {
  const r = Math.round((Number(v) || 0) * 100) / 100;
  const abs = Math.abs(r).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return r < 0 ? `${MINUS}$${abs}` : `$${abs}`;
};
const whole = (v) => {
  const r = Math.round(Number(v) || 0);
  const abs = Math.abs(r).toLocaleString('en-US');
  return r < 0 ? `${MINUS}$${abs}` : `$${abs}`;
};

const cell = { padding: '0 5px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
const label = { fontSize: 10.5, fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase', color: GREY };
// The project takes what's left (236px); its invoice count is on the line under its name.
const P_COLS = [
  { key: 'project', label: 'Project', w: null },
  { key: 'invoiced', label: 'Invoiced', w: 108, right: true },
  { key: 'collected', label: 'Collected', w: 108, right: true },
  { key: 'open', label: 'Open', w: 108, right: true },
  { key: 'days', label: 'Days', w: 50, right: true },
  { key: 'status', label: 'Status', w: 86 },
];
// Money columns fit a 7-figure credit at 11px Geist Mono: "−$1,428,245.90" (live, 10-15) is 93px + 10.
const I_COLS = [
  { key: 'number', label: 'Invoice', w: 66 },
  { key: 'date', label: 'Date', w: 82 },
  { key: 'due', label: 'Due', w: 82 },
  { key: 'amount', label: 'Amount', w: 106, right: true },
  { key: 'collected', label: 'Collected', w: 106, right: true },
  { key: 'open', label: 'Open', w: 106, right: true },
  { key: 'days', label: 'Days', w: 44, right: true },
  { key: 'status', label: 'Status', w: null },
];
const colStyle = (c) => ({ ...cell, width: c.w || undefined, flex: c.w ? 'none' : 1, minWidth: 0, textAlign: c.right ? 'right' : 'left' });
const Tr = ({ h, children, style }) => (
  <div style={{ display: 'flex', alignItems: 'center', height: h, boxSizing: 'border-box', borderBottom: `1px solid ${LINE}`, ...style }}>{children}</div>
);

const Tag = () => <span style={{ border: `1.5px solid ${RED}`, color: RED, fontSize: 11, fontWeight: 600, letterSpacing: '.12em', padding: '2px 7px', lineHeight: '14px' }}>CONFIDENTIAL</span>;

const Block = ({ it, totals, customers }) => {
  if (it.kind === 'title') {
    const isProjects = it.section === 'projects';
    return (
      <div style={{ height: H.title, boxSizing: 'border-box', paddingTop: 18, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12 }}>
        <div style={{ fontSize: 12, lineHeight: '20px', fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase', color: GREEN_HEAD }}>
          {isProjects ? 'Projects' : 'Invoices'}{it.continued && <span style={{ fontWeight: 400, letterSpacing: 0, textTransform: 'none', color: GREY }}> (continued)</span>}
        </div>
        <div style={{ fontSize: 11, lineHeight: '20px', color: GREY }}>{isProjects ? 'Days: average days to pay (red over 60)' : 'Days: invoice date to final payment'}</div>
      </div>
    );
  }
  if (it.kind === 'thead') {
    const cols = it.section === 'projects' ? P_COLS : I_COLS;
    return (
      <Tr h={H.thead} style={{ borderBottom: `1.5px solid ${INK2}` }}>
        {cols.map((c) => <div key={c.key} style={{ ...colStyle(c), ...label, color: INK2 }}>{c.label}</div>)}
      </Tr>
    );
  }
  if (it.kind === 'project') {
    const { j } = it;
    const own = payStatsOf(j.pay);
    const st = projectStatus(j);
    const cust = customers.get(customerOfJob(j));
    return (
      <Tr h={H.project} style={{ fontSize: 11 }}>
        <div style={{ ...colStyle(P_COLS[0]), lineHeight: '15px' }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: INK, overflow: 'hidden', textOverflow: 'ellipsis' }}>{j.name}</div>
          <div style={{ fontSize: 10.5, color: GREY, overflow: 'hidden', textOverflow: 'ellipsis' }}>#{j.job} · {j.t.count} invoice{j.t.count === 1 ? '' : 's'}{j.pm.length ? ` · PM ${j.pm.join(', ')}` : ''}</div>
        </div>
        <div style={{ ...colStyle(P_COLS[1]), fontFamily: MONO }}>{money(j.t.invoiced)}</div>
        <div style={{ ...colStyle(P_COLS[2]), fontFamily: MONO, color: INK2 }}>{money(j.t.collected)}</div>
        <div style={{ ...colStyle(P_COLS[3]), fontFamily: MONO, fontWeight: 600, color: j.t.pastDue > 0 ? RED : INK }}>{Math.abs(j.t.open) > 0.004 ? money(j.t.open) : '–'}</div>
        <div style={{ ...colStyle(P_COLS[4]), fontFamily: MONO, color: own && own.avg > SLOW_DAYS ? RED : INK }} title={cust ? `Customer ${cust.avg} days over ${cust.jobs} job(s)` : undefined}>{own ? own.avg : '–'}</div>
        <div style={{ ...colStyle(P_COLS[5]), color: TONE[st.tone], fontWeight: 600 }}>{st.label}</div>
      </Tr>
    );
  }
  if (it.kind === 'ptotal') {
    return (
      <Tr h={H.ptotal} style={{ fontSize: 11, fontWeight: 600, background: FILL, borderTop: `1.5px solid ${INK2}` }}>
        <div style={{ ...colStyle(P_COLS[0]), fontFamily: SANS, fontSize: 11.5 }}>Total · {totals.count} invoice{totals.count === 1 ? '' : 's'}</div>
        <div style={{ ...colStyle(P_COLS[1]), fontFamily: MONO }}>{money(totals.invoiced)}</div>
        <div style={{ ...colStyle(P_COLS[2]), fontFamily: MONO }}>{money(totals.collected)}</div>
        <div style={{ ...colStyle(P_COLS[3]), fontFamily: MONO, color: totals.pastDue > 0 ? RED : INK }}>{money(totals.open)}</div>
        <div style={colStyle(P_COLS[4])} />
        <div style={colStyle(P_COLS[5])} />
      </Tr>
    );
  }
  if (it.kind === 'group') {
    const { j } = it;
    return (
      <Tr h={H.group} style={{ fontSize: 11.5, background: FILL }}>
        <div style={{ ...cell, flex: 1, minWidth: 0 }}>
          <span style={{ fontWeight: 600, color: INK }}>{j.name}</span>
          <span style={{ color: GREY }}> · #{j.job} · {j.t.count} invoice{j.t.count === 1 ? '' : 's'}</span>
        </div>
        <div style={{ ...cell, fontFamily: MONO, color: GREY, flex: 'none' }}>{money(j.t.invoiced)}</div>
      </Tr>
    );
  }
  const { i } = it;
  const st = statusMeta(i);
  const d = daysToPay(i);
  return (
    <Tr h={H.inv} style={{ fontSize: 11 }}>
      <div style={{ ...colStyle(I_COLS[0]), fontFamily: MONO }}>{i.number}</div>
      <div style={colStyle(I_COLS[1])}>{fmtDay(dateOf(i))}</div>
      <div style={{ ...colStyle(I_COLS[2]), color: INK2 }}>{i.due ? fmtDay(i.due) : '–'}</div>
      <div style={{ ...colStyle(I_COLS[3]), fontFamily: MONO }}>{money(i.amount)}</div>
      <div style={{ ...colStyle(I_COLS[4]), fontFamily: MONO, color: INK2 }}>{Math.abs(i.collected) > 0.004 ? money(i.collected) : '–'}</div>
      <div style={{ ...colStyle(I_COLS[5]), fontFamily: MONO, fontWeight: 600 }}>{Math.abs(i.open) > 0.004 ? money(i.open) : '–'}</div>
      <div style={{ ...colStyle(I_COLS[6]), fontFamily: MONO, color: d != null && d > SLOW_DAYS ? RED : INK }}>{d == null ? '–' : d}</div>
      <div style={{ ...colStyle(I_COLS[7]), color: TONE[st.tone], fontWeight: 600 }}>{st.label}</div>
    </Tr>
  );
};

const Sheet = ({ page, pages, footer, children }) => (
  <div className="psr-sheet ihr-sheet" style={{ width: PAGE_W, height: PAGE_H, boxSizing: 'border-box', background: '#fff', color: INK, fontFamily: SANS, fontSize: 13, lineHeight: 1.4, position: 'relative', padding: `${PAD_TOP}px ${PAD_X}px 0`, overflow: 'hidden' }}>
    {children}
    <div style={{ position: 'absolute', left: PAD_X, right: PAD_X, bottom: 40, display: 'flex', justifyContent: 'space-between', gap: 16, paddingTop: 8, borderTop: `1px solid ${LINE}`, fontSize: 11.5, lineHeight: '16px', color: GREY }}>
      <div style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}><span style={{ color: RED, fontWeight: 600, letterSpacing: '.08em' }}>CONFIDENTIAL</span> · {footer}</div>
      <div style={{ whiteSpace: 'nowrap' }}>Page {page} of {pages}</div>
    </div>
  </div>
);

const Figure = ({ title, value, sub, color }) => (
  <div style={{ background: FILL, padding: '9px 10px', minWidth: 0, height: 76, boxSizing: 'border-box' }}>
    <div style={{ ...label, fontSize: 10, color: INK2 }}>{title}</div>
    {/* 15px: "$239,942,500" (every job, to date) is 108px of the tile's 115. */}
    <div style={{ fontFamily: MONO, fontSize: 15, lineHeight: '26px', fontWeight: 800, color: color || INK, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden' }}>{value}</div>
    <div style={{ fontSize: 10.5, lineHeight: '14px', color: GREY, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sub}</div>
  </div>
);

const InvoiceReportPage = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [fontsReady, setFontsReady] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [sentMsg, setSentMsg] = useState('');
  const [xlsxBusy, setXlsxBusy] = useState(false);
  const [xlsxErr, setXlsxErr] = useState('');
  const pagesRef = useRef(null);
  const paramString = params.toString();

  useEffect(() => {
    let alive = true;
    getInvoiceHistory()
      .then((d) => { if (alive) setData(d); })
      .catch((e) => { if (alive) setError(e.message || 'Could not load the invoice history.'); });
    loadPsrFonts().then(() => { if (alive) setFontsReady(true); });
    return () => { alive = false; };
  }, []);
  useEffect(() => {
    document.body.classList.add('ihr-open');
    return () => document.body.classList.remove('ihr-open');
  }, []);

  // The page's choices; a period without a date opens on the latest one with invoices, as the page does.
  const view = useMemo(() => {
    const v = viewFromParams(new URLSearchParams(paramString));
    if (v.periodType !== 'all' && !v.periodAt && data) {
      const today = todayCentral();
      const latest = (data.jobs || []).flatMap((j) => j.invoices.map(dateOf)).filter((d) => d && d <= today).sort().pop() || today;
      v.periodAt = periodKey(latest, v.periodType);
    }
    return v;
  }, [paramString, data]);
  const sel = useMemo(() => (data ? selectView(data.jobs || [], view) : null), [data, view]);
  const about = useMemo(() => (data ? describeView(view, data) : null), [data, view]);
  const pages = useMemo(() => (sel && sel.visible.length ? paginateFlow(buildFlow(sel.visible, !!view.job)) : []), [sel, view.job]);
  const ready = !!(sel && fontsReady && pages.length);

  useEffect(() => {
    const prev = document.title;
    if (about) document.title = `Invoice History — ${about.title}`;
    return () => { document.title = prev; };
  }, [about]);

  const downloadExcel = async () => {
    if (!data || xlsxBusy) return;
    setXlsxBusy(true); setXlsxErr(''); setSentMsg('');
    try {
      const { downloadInvoiceWorkbook } = await import('../lib/invoiceXlsx'); // ExcelJS only when asked
      await downloadInvoiceWorkbook(data, view);
    } catch (e) { setXlsxErr(e.message || 'The workbook couldn’t be built.'); } finally { setXlsxBusy(false); }
  };

  const asOf = data ? fmtDay(data.as_of) : '';
  const footer = about ? `E3 Entegral Solutions · Invoice history · ${about.title} · Generated ${fmtDay(todayCentral())}` : '';
  const filename = about ? `Invoice History ${view.job || about.period} ${todayCentral()}.pdf`.replace(/[\\/:*?"<>|]+/g, ' ') : 'Invoice History.pdf';
  const t = sel ? sel.totals : null;
  const pay = sel ? sel.pay : null;

  const css = `
.ihr-shell{position:fixed;inset:0;z-index:3000;overflow:auto;background:#E6E3DE;font-family:${SANS};color:${INK};-webkit-font-smoothing:antialiased;letter-spacing:normal}
.ihr-bar{position:sticky;top:0;z-index:2;display:flex;align-items:center;gap:12px;padding:10px 20px;background:rgba(255,255,255,.94);backdrop-filter:blur(8px);border-bottom:1px solid #E0DDD8}
.ihr-bar .t{flex:1;min-width:0;font-size:13.5px;color:${GREY};white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.psr-btn{display:inline-flex;align-items:center;gap:7px;height:34px;padding:0 14px;border-radius:8px;font:600 13.5px ${SANS};cursor:pointer;border:1px solid #E0DDD8;background:#fff;color:${INK}}
.psr-btn.primary{background:${GREEN_SPENT};border-color:${GREEN_SPENT};color:#fff}
.psr-btn:disabled{opacity:.55;cursor:default}
.ihr-pages{display:flex;flex-direction:column;align-items:center;gap:24px;padding:24px 16px 48px}
.ihr-pages .ihr-sheet{box-shadow:0 1px 2px rgba(0,0,0,.06),0 12px 32px -12px rgba(0,0,0,.18);flex:none}
@page{size:letter;margin:0}
@media print{
  body.ihr-open #root{display:none!important}
  html,body{background:#fff!important;margin:0!important}
  .ihr-shell{position:static;overflow:visible;background:none}
  .ihr-bar,.psr-dialog{display:none!important}
  .ihr-pages{display:block;padding:0}
  .ihr-pages .ihr-sheet{box-shadow:none;break-after:page}
  .ihr-pages .ihr-sheet:last-child{break-after:auto}
  *{-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important}
}`;

  return createPortal(
    <div className="ihr-shell">
      <style>{css}</style>
      <div className="ihr-bar">
        <button type="button" className="psr-btn" onClick={() => navigate('/ar?view=history')}>← AR / Collections</button>
        <div className="t">{about ? `Invoice history · ${about.title}` : 'Invoice history'}</div>
        {sentMsg && <span role="status" style={{ fontSize: 13.5, fontWeight: 600, color: GREEN_GOOD, whiteSpace: 'nowrap' }}>{sentMsg}</span>}
        {xlsxErr && <span role="alert" style={{ fontSize: 13.5, color: RED, whiteSpace: 'nowrap' }}>{xlsxErr}</span>}
        <button type="button" className="psr-btn" disabled={!ready} onClick={() => window.print()}>Print / Save as PDF</button>
        <button type="button" className="psr-btn" disabled={!data || xlsxBusy} onClick={downloadExcel}>{xlsxBusy ? 'Building…' : 'Download Excel'}</button>
        <button type="button" className="psr-btn primary" disabled={!ready} onClick={() => { setSentMsg(''); setEmailOpen(true); }}>Email report</button>
      </div>
      {error && <div style={{ padding: 40, textAlign: 'center', color: RED }}>{error}</div>}
      {!error && !sel && <div style={{ padding: 60, textAlign: 'center', color: GREY }}>Building the report…</div>}
      {sel && sel.visible.length === 0 && <div style={{ padding: 60, textAlign: 'center', color: GREY }}>No invoices match this period and these filters.</div>}
      {ready && (
        <div className="ihr-pages" ref={pagesRef}>
          {pages.map((items, pi) => (
            <Sheet key={pi} page={pi + 1} pages={pages.length} footer={footer}>
              {pi === 0 ? (
                <>
                  <div style={{ height: 96, boxSizing: 'border-box', borderBottom: `2px solid ${GREEN_HEAD}`, paddingBottom: 10, display: 'flex', gap: 16, alignItems: 'center' }}>
                    {/* The E3 logo on AR's exports (Duncan 2026-10-15); lib/psrPdf draws it into the emailed PDF. */}
                    <img src="/e3-logo.png" alt="E3 Entegral Solutions" width={64} height={64} style={{ display: 'block', width: 64, height: 64, flex: 'none' }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ height: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: 12, fontWeight: 600, letterSpacing: '.08em', color: GREEN_HEAD }}>E3 ENTEGRAL SOLUTIONS · INVOICE HISTORY</span>
                      <Tag />
                    </div>
                    <div style={{ marginTop: 8, height: 34, fontSize: 26, lineHeight: '34px', fontWeight: 600, letterSpacing: '-.01em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{about.title}</div>
                    <div style={{ marginTop: 4, height: 18, display: 'flex', justifyContent: 'space-between', gap: 16, fontSize: 12, lineHeight: '18px', color: INK2 }}>
                      <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {about.job
                          ? `#${about.job.job}${about.job.pm.length ? ` · PM ${about.job.pm.join(', ')}` : ''}${about.job.cm.length ? ` · CM ${about.job.cm.join(', ')}` : ''} · ${about.period}`
                          : `${about.filters} · ${sel.visible.length} project${sel.visible.length === 1 ? '' : 's'}`}
                      </span>
                      <span style={{ whiteSpace: 'nowrap', color: GREY }}>AR data as of {asOf}</span>
                    </div>
                    </div>
                  </div>
                  <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: 5 }}>
                    <Figure title="Invoiced" value={whole(t.invoiced)} sub={`${t.count} invoice${t.count === 1 ? '' : 's'} · ${sel.visible.length} job${sel.visible.length === 1 ? '' : 's'}`} />
                    <Figure title="Collected" value={whole(t.collected)} color={GREEN_GOOD} sub={t.invoiced > 0 ? `${((t.collected / t.invoiced) * 100).toFixed(1)}% of it` : ''} />
                    <Figure title="Open" value={whole(t.open)} sub={`${t.openCount} invoice${t.openCount === 1 ? '' : 's'}`} />
                    <Figure title="Past due" value={whole(t.pastDue)} color={t.pastDue > 0 ? RED : undefined} sub={`${t.pastDueCount} invoice${t.pastDueCount === 1 ? '' : 's'}`} />
                    <Figure title="Avg days to pay" value={pay.all ? `${pay.all.avg} days` : '–'} sub={pay.all ? `median ${pay.all.median} · ${pay.all.n} paid` : 'none paid yet'} />
                  </div>
                </>
              ) : (
                <div style={{ height: RUN_HEAD - 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, borderBottom: `2px solid ${GREEN_HEAD}`, boxSizing: 'content-box', fontSize: 12, color: GREY }}>
                  <div style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <span style={{ fontWeight: 600, letterSpacing: '.08em', color: GREEN_HEAD }}>INVOICE HISTORY</span> · <span style={{ color: INK }}>{about.title}</span>
                  </div>
                  <Tag />
                </div>
              )}
              {items.map((it, k) => <Block key={k} it={it} totals={t} customers={pay.customers} />)}
            </Sheet>
          ))}
        </div>
      )}
      {emailOpen && ready && (
        <EmailReportDialog
          heading="Email this report"
          subheading={`${about.title} · E3 people only`}
          filename={filename}
          pages={pages.length}
          buildPdf={async () => {
            const { buildPsrPdf } = await import('../lib/psrPdf');
            return buildPsrPdf(pagesRef.current, { title: `Invoice history — ${about.title}`, author: 'E3 Entegral Solutions' });
          }}
          send={(payload) => emailInvoiceReport({
            ...payload,
            link: `/ar/invoice-report?${paramString}`,
            summary: {
              title: about.title, period: about.period, invoiced: whole(t.invoiced), collected: whole(t.collected),
              open: whole(t.open), pastDue: whole(t.pastDue), asOf,
            },
          })}
          onClose={() => setEmailOpen(false)}
          onSent={(n) => { setEmailOpen(false); setSentMsg(`Sent to ${n} ${n === 1 ? 'person' : 'people'}`); }}
        />
      )}
    </div>,
    document.body,
  );
};

export default InvoiceReportPage;
